import { useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import PaiementAbonnementModal from "@/components/feature/PaiementAbonnementModal";
import { useSession } from "@/session/SessionProvider";
import { homePathForUser } from "@/session/types";
import { getSafeUser } from "@/store/shared/supabase";
import { type FormuleId, type Periodicite } from "@/config/formules";
import { useFormules } from "@/store/formules/FormulesProvider";
import { getMethodePaiement, type PaiementMethode } from "@/config/paiement";
import { soumettrePaiementInscriptionRpc } from "@/store/platform/paiementAbonnement";
import { finaliserInscriptionSiBesoin } from "@/store/platform/inscriptionPaiement";
import { formatFcfa } from "@/utils/format";
import { cn } from "@/utils/cn";

interface Intent {
  formule: FormuleId;
  periodicite: Periodicite;
  mode: PaiementMethode | "";
  reference: string;
}

const FORMULE_IDS: FormuleId[] = ["gratuit", "essentiel", "professionnel", "agence"];

function toFormuleId(value: unknown): FormuleId | null {
  return typeof value === "string" && (FORMULE_IDS as string[]).includes(value) ? (value as FormuleId) : null;
}

function toMode(value: unknown): PaiementMethode | "" {
  return value === "orange_money" || value === "moov_money" || value === "especes" ? value : "";
}

/**
 * Finalisation du paiement à la PREMIÈRE CONNEXION.
 *
 * La confirmation e-mail empêche toute session pendant l'inscription : le choix
 * de formule, la période, le moyen et la référence ont été enregistrés dans les
 * métadonnées du compte. Ici, le client (désormais authentifié) joint sa preuve
 * et envoie la demande de paiement, rattachée côté serveur à SON compte.
 */
export default function FinaliserPaiementPage() {
  const { session, loading } = useSession();
  // Prix lus depuis Supabase (source de vérité) : le montant affiché reste
  // aligné sur le prix serveur, même si le Super Admin le modifie.
  const { getFormule } = useFormules();
  const [intent, setIntent] = useState<Intent | null>(null);
  const [checking, setChecking] = useState(true);
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  const autoSubmittedRef = useRef(false);

  useEffect(() => {
    let active = true;
    (async () => {
      const user = await getSafeUser();
      const meta = (user?.user_metadata ?? {}) as Record<string, unknown>;
      const formule = toFormuleId(meta.formule);
      const periodicite: Periodicite = meta.periodicite === "annuel" ? "annuel" : "mensuel";
      const mode = toMode(meta.mode_paiement);
      const reference = typeof meta.reference === "string" ? meta.reference : "";
      const intentValue: Intent | null = formule ? { formule, periodicite, mode, reference } : null;
      if (active && intentValue) setIntent(intentValue);

      // Transmission AUTOMATIQUE de la demande de paiement à la première connexion :
      // la preuve choisie à l'inscription a été mise en cache navigateur, le client
      // ne re-fournit donc jamais rien. Le serveur reste seul juge (montant/formule).
      // Idempotent : jamais de doublon (une demande existante est détectée).
      if (active && !autoSubmittedRef.current) {
        autoSubmittedRef.current = true;
        const resultat = await finaliserInscriptionSiBesoin();
        if (active && (resultat === "envoye" || resultat === "deja")) setDone(true);
      }

      if (active) setChecking(false);
    })();
    return () => {
      active = false;
    };
  }, []);

  if (loading || checking) {
    return (
      <section className="flex min-h-[60vh] items-center justify-center bg-background-50 px-4">
        <div className="flex flex-col items-center gap-3">
          <span className="h-8 w-8 animate-spin rounded-full border-2 border-background-200 border-t-primary-500" />
          <p className="text-sm text-foreground-500">Chargement de votre paiement…</p>
        </div>
      </section>
    );
  }

  if (!session) {
    return <Navigate to="/connexion" replace />;
  }

  // Compte déjà actif : rien à finaliser ici, on l'oriente vers son espace.
  if (session.statut === "actif") {
    return <Navigate to={homePathForUser(session)} replace />;
  }

  const formule = intent ? getFormule(intent.formule) : null;
  const isPaid = Boolean(formule && formule.prixMensuel > 0);
  const montant = formule
    ? intent?.periodicite === "annuel"
      ? formule.prixAnnuel
      : formule.prixMensuel
    : 0;
  const methodeInfo = intent?.mode ? getMethodePaiement(intent.mode) : null;

  return (
    <section className="px-4 py-14 md:py-20 bg-background-50">
      <div className="container-page max-w-xl animate-fade-up">
        {done ? (
          <div className="text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
              <i className="ri-checkbox-circle-line text-3xl" aria-hidden="true" />
            </span>
            <h1 className="mt-6 text-2xl md:text-3xl font-heading font-semibold text-foreground-950">
              Demande de paiement enregistrée
            </h1>
            <p className="mt-3 text-sm md:text-base text-foreground-600">
              Votre demande de paiement est <strong className="font-semibold text-foreground-900">en attente de
              vérification</strong>. Votre compte sera activé après validation par notre équipe, selon le processus
              prévu.
            </p>
            <Link
              to="/connexion"
              className="mt-8 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-label font-medium text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-login-box-line" aria-hidden="true" />
              Aller à la connexion
            </Link>
          </div>
        ) : !intent || !isPaid || !formule ? (
          <div className="text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-accent-100 text-accent-700">
              <i className="ri-time-line text-2xl" aria-hidden="true" />
            </span>
            <h1 className="mt-6 text-2xl md:text-3xl font-heading font-semibold text-foreground-950">
              Compte en attente de validation
            </h1>
            <p className="mt-3 text-sm md:text-base text-foreground-600">
              {formule && formule.prixMensuel === 0
                ? "Votre formule Gratuite ne nécessite aucun paiement. Votre compte sera activé dès sa validation par un administrateur."
                : "Aucune demande de paiement n'est associée à ce compte. Votre inscription reste en attente de validation."}
            </p>
            <Link
              to="/"
              className="mt-8 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-3 text-sm font-label font-medium text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line" aria-hidden="true" />
              Retour à l'accueil
            </Link>
          </div>
        ) : (
          <>
            <div className="text-center">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                <i className="ri-secure-payment-line text-2xl" aria-hidden="true" />
              </span>
              <h1 className="mt-6 text-2xl md:text-3xl font-heading font-semibold text-foreground-950">
                Finalisez votre paiement
              </h1>
              <p className="mx-auto mt-3 max-w-md text-sm md:text-base text-foreground-600">
                Dernière étape : joignez votre preuve de paiement. Votre compte sera activé après vérification par
                notre équipe.
              </p>
            </div>

            <div className="mt-8 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <dl className="flex flex-col gap-3">
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-sm text-foreground-600">Formule</dt>
                  <dd className="text-sm font-label font-medium text-foreground-950">{formule.nom}</dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-sm text-foreground-600">Période</dt>
                  <dd className="text-sm font-label font-medium text-foreground-950">
                    {intent.periodicite === "annuel" ? "Annuelle" : "Mensuelle"}
                  </dd>
                </div>
                <div className="flex items-center justify-between gap-3 border-t border-background-200 pt-3">
                  <dt className="text-sm text-foreground-600">Montant à payer</dt>
                  <dd className="font-heading text-lg font-semibold text-primary-700">{formatFcfa(montant)}</dd>
                </div>
                {methodeInfo && (
                  <div className="flex items-center justify-between gap-3">
                    <dt className="text-sm text-foreground-600">Moyen de paiement</dt>
                    <dd className="flex items-center gap-2 text-sm font-label font-medium text-foreground-950">
                      <i className={cn(methodeInfo.icon, "text-base text-primary-600")} aria-hidden="true" />
                      {methodeInfo.label}
                      {methodeInfo.numero && <span className="text-foreground-500">· {methodeInfo.numero}</span>}
                    </dd>
                  </div>
                )}
              </dl>

              <button
                type="button"
                onClick={() => setOpen(true)}
                className="mt-6 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-label font-medium text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-upload-2-line text-base" aria-hidden="true" />
                Joindre la preuve et envoyer
              </button>
              <p className="mt-3 text-center text-xs text-foreground-500">
                Votre demande restera « en attente » jusqu'à la validation par un administrateur.
              </p>
            </div>
          </>
        )}
      </div>

      {intent && isPaid && formule && (
        <PaiementAbonnementModal
          open={open}
          onClose={() => setOpen(false)}
          formuleCode={intent.formule}
          formuleNom={formule.nom}
          periodicite={intent.periodicite}
          montant={montant}
          initialMethode={intent.mode}
          initialReference={intent.reference}
          submitPaiement={soumettrePaiementInscriptionRpc}
          onSuccess={() => setDone(true)}
        />
      )}
    </section>
  );
}